import React, { useState } from 'react';
import {
    GetFormalOffersResponse,
    fetchGetDrawDownRepaymentScheduleCreditFacility,
    LapiError,
    GetDrawDownRepaymentScheduleCreditFacilityResponse
} from '@iwoca/lapi-client/edge';
import { AlertBox, Button, Card, Icon, Spinner, Tooltip } from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import classNames from 'classnames';
import { intlFormatCurrency } from '@iwIntlProvider/FormattedCurrency';
import { FormattedMessage, useIntl } from 'react-intl';
import styles from './flexiInterestOnlyCard.module.css';
import messages from './flexiInterestOnlyCard.messages';
import FlexiScheduleModal from '../flexiScheduleModal/flexiScheduleModal';
import Fetching from '../../../../ui/fetching/fetching';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { REQUEST_CHANGE_ROUTER } from '@iwRouter/IwRouter.actions';
import { buildLocationWithSearch } from '../../../../drawDown/DrawDownHelper';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    startDrawdown: (offerId: string, amount: number) => {
        dispatch({
            type: REQUEST_CHANGE_ROUTER,
            //@ts-ignore
            location: buildLocationWithSearch({
                pathname: '/funding-requirements/',
                offerId: offerId,
                amount: amount
            })
        });
    }
});

type TFlexiCard = {
    offer: NonNullable<
        GetFormalOffersResponse['data']
    >['formal_offers'][number];
    amount?: number;
} & ReturnType<typeof mapDispatchToProps>;

type TSchedule = GetDrawDownRepaymentScheduleCreditFacilityResponse;

const FlexiInterestOnlyCard = ({
    offer,
    amount,
    startDrawdown
}: TFlexiCard) => {
    const stateKey = useStateKey();
    const intl = useIntl();
    const minAmount = 150;

    const [openSchedule, setOpenSchedule] = useState(false);
    const [collapsed, setCollapsed] = useState(true);
    const amountInRange =
        !!amount && amount >= minAmount && amount <= offer.max_amount!;
    const disabled = !amountInRange;

    const { data: schedule, isFetching } = useQuery<TSchedule, LapiError>({
        queryKey: [
            'drawDownSchedule',
            stateKey,
            offer?.offer_id,
            amount,
            minAmount,
            offer.max_amount
        ],
        queryFn: (): Promise<TSchedule> => {
            const amountInRange =
                !!amount && amount >= minAmount && amount <= offer.max_amount!;

            if (!amountInRange) {
                return new Promise((res) => res(schedule!));
            } else {
                return fetchGetDrawDownRepaymentScheduleCreditFacility({
                    stateKey: stateKey,
                    offerId: offer?.offer_id!,
                    query: {
                        amount: amount!,
                        earlyFinalRepaymentDate: undefined
                    }
                });
            }
        },
        enabled: !!amount,
        keepPreviousData: true
    });

    if (!amount || !schedule) {
        return (
            <Card className={styles.card}>
                <Spinner className={styles.spinner} />
            </Card>
        );
    }
    return (
        <>
            <Tooltip
                // @ts-expect-error
                content={
                    <div className={styles.content}>
                        <FormattedMessage
                            {...messages.disabledTooltip}
                            values={{
                                min: intlFormatCurrency({
                                    intl,
                                    value: minAmount
                                }),
                                max: intlFormatCurrency({
                                    intl,
                                    value: offer.max_amount!
                                })
                            }}
                        />
                    </div>
                }
                open={disabled ? undefined : false}
                className={styles.tooltip}
            >
                <Card
                    data-testid={`flexiCard_${offer.offer_id}`}
                    className={classNames(styles.card, {
                        [styles.disabled]: disabled
                    })}
                >
                    <h1 className={styles.title}>
                        <FormattedMessage
                            {...messages.title}
                            values={{
                                ioPeriod: offer.interest_only_repayments,
                                button: (chunks) => (
                                    <button
                                        className={styles.titleButton}
                                        onClick={() => setOpenSchedule(true)}
                                    >
                                        {chunks}
                                    </button>
                                )
                            }}
                        />
                    </h1>
                    <AlertBox variant="success" className={styles.alert}>
                        <Icon icon="starFilled" className={styles.alertIcon} />
                        <span>
                            <FormattedMessage
                                {...messages.alert}
                                values={{
                                    bold: (chunks) => <strong>{chunks}</strong>
                                }}
                            />
                        </span>
                    </AlertBox>
                    <div className={styles.info}>
                        <div className={styles.infoRow}>
                            <div>
                                <FormattedMessage {...messages.interest} />
                            </div>
                            <div>
                                <FormattedMessage
                                    {...messages.interestAmount}
                                    values={{
                                        interest: intl.formatNumber(
                                            Math.round(
                                                (offer.interest_rates?.[0]
                                                    .rate || NaN) * 10000
                                            ) / 100
                                        ),
                                        fetching: (chunks) => (
                                            <Fetching isFetching={isFetching}>
                                                {chunks}
                                            </Fetching>
                                        )
                                    }}
                                />
                            </div>
                        </div>
                        <div className={styles.infoRow}>
                            <div>
                                <FormattedMessage {...messages.repayments} />
                            </div>
                            <div>
                                <FormattedMessage
                                    {...messages.repaymentAmount}
                                    values={{
                                        amountIO: intlFormatCurrency({
                                            intl,
                                            value:
                                                schedule?.data.schedule[0]
                                                    .total || 0
                                        }),
                                        amount: intlFormatCurrency({
                                            intl,
                                            value:
                                                schedule?.data.schedule[4]
                                                    .total || 0
                                        }),
                                        fetching: (chunks) => (
                                            <Fetching isFetching={isFetching}>
                                                {chunks}
                                            </Fetching>
                                        )
                                    }}
                                />
                            </div>
                            <Button
                                variant="tertiary"
                                size="small"
                                className={styles.viewSchedule}
                                onClick={() => setOpenSchedule(true)}
                            >
                                <FormattedMessage {...messages.viewSchedule} />
                            </Button>
                        </div>
                        <div className={styles.infoRow}>
                            <div>
                                <FormattedMessage {...messages.total} />
                            </div>
                            <div>
                                <Fetching isFetching={isFetching}>
                                    {intlFormatCurrency({
                                        intl,
                                        value:
                                            schedule?.data.summary
                                                .total_payable || 0
                                    })}
                                </Fetching>
                            </div>
                        </div>
                    </div>

                    <div
                        className={classNames(styles.collapsableContainer, {
                            [styles.collapsed]: collapsed
                        })}
                    >
                        <div
                            className={styles.collapsable}
                            aria-hidden={collapsed}
                        >
                            <p className={styles.customiseMessage}>
                                <FormattedMessage
                                    {...messages.customiseMessage}
                                />
                            </p>
                        </div>
                        <div className={styles.collapseControll}>
                            <Button
                                variant="tertiary"
                                onClick={() => setCollapsed(!collapsed)}
                            >
                                <FormattedMessage {...messages.customise} />
                                <Icon
                                    icon={collapsed ? 'angleDown' : 'angleUp'}
                                />
                            </Button>

                            <Button
                                onClick={() =>
                                    offer.offer_id &&
                                    startDrawdown(offer.offer_id, amount)
                                }
                            >
                                <FormattedMessage {...messages.continue} />
                            </Button>
                        </div>
                    </div>
                </Card>
            </Tooltip>
            <FlexiScheduleModal
                schedule={schedule.data.schedule}
                isOpen={openSchedule}
                onClose={() => setOpenSchedule(false)}
            />
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(FlexiInterestOnlyCard);
